import {distinctUntilChanged, filter, finalize, map, startWith, switchMap, switchMapTo, take, tap, withLatestFrom} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewContainerRef} from '@angular/core';
import {BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject} from 'rxjs';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Location} from '@angular/common';
import {Router} from '@angular/router';

import {UnitPaymentService} from '../unit-payment.service';
import {TerraUtils} from '../../../../TerraUtils';
import {PaymentType} from '../../../../enums/payment-type.enum';
import {UnitPaymentStatus} from '../models/unit-payment-status.enum';
import {UnitPaymentPurpose} from '../models/unit-payment-purpose.enum';
import {UnitPaymentCategory} from '../models/unit-payment-category.enum';
import {RoutingService} from '../../../../../services/routing.service';
import {LoggerService} from '../../../../errors/logger.service';
import {PaymentCreateDto} from '../models/payment/payment-create-dto.model';
import {TerraNumberPipe} from '../../../../pipes/TerraNumber.pipe';
import {RecipientCreateDto} from '../models/recipient/recipient-create-dto.model';
import {CustomValidators} from '../../../../validators/custom.validators';
import {UnitBankAccountListItem} from '../../../../models/gp/UnitBankAccountListItem.model';
import {DestinationBankAccountCreateDto} from '../models/recipient/recipient-bank-account-create-dto.model';
import {SelectExistingRecipientDialogComponent} from './select-existing-recipient-dialog/select-existing-recipient-dialog.component';
import {BaseResponseDto} from '../../../../models/BaseResponseDto.model';
import {UtilsService} from '../../../../../services/utils.service';
import {RecipientReadDto} from 'src/app/shared/models/recipient-read-dto.model';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import UnitAccountProvider from '../../../../enums/UnitAccountProvider.enum';
import {UserService} from '../../../../../services/shared/user.service';
import {RadioBtn} from '../../../form/radio-buttons/radio-buttons.component';
import {GeneralSettingsService} from 'src/app/services/shared/general-settings.service';
import {UnitAccountLimits} from 'src/app/shared/models/unit-bank-account/UnitAccountLimits.model';
import {UnitBankAccountLimitDialogComponent, UnitBankAccountLimitDialogParams} from '../../bank-account-limits/unit-bank-account-limit-dialog/unit-bank-account-limit-dialog.component';
import AccountType from 'src/app/shared/enums/AccountType.enum';
import {AddressFields, AddressFormSettings} from '../../../address-form/address-form.settings';
import {LocationDetailsResponse} from 'src/app/shared/models/LocationDetailsResponse.model';
import {HelpSourceTypeService} from 'src/app/services/help-source-type.service';

enum AccountsSource {
  Unit,
  NewExternal,
  External
}

@Component({
  selector: 'terra-unit-create-edit-payment',
  templateUrl: './unit-create-edit-payment.component.html',
  styleUrls: ['./unit-create-edit-payment.component.scss', '../../unit-shared.scss'],
  providers: [UnitPaymentService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitCreateEditPaymentComponent extends OnDestroyMixin implements OnInit {
  AccountsSource = AccountsSource;
  UnitApplicationParentType = UnitApplicationParentType;
  private _idempotencyKey: string;
  /**
   * Changes when this component executes HTTP request, effects the loader on the HTML.
   */
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _existingPayment$ = this.unitPaymentService.payment$;

  readonly forbiddenNameCharacters = this.unitPaymentService.forbiddenNameCharacters;
  private _defaultSelector = `Internal`;
  private _isRepeated = false;

  paths = TerraUtils.consts.paths;
  accountSelectorTitle = this._defaultSelector;
  paymentMethodDetails: RadioBtn[] = [];
  accountsSource: RadioBtn[] = [
    {
      value: AccountsSource.NewExternal,
      text: 'New recipient',
      hint: null,
      disabled: false,
      checked: false
    },
    {
      value: AccountsSource.External,
      text: 'Existing recipient',
      hint: null,
      disabled: false,
      checked: false
    },
    {
      value: AccountsSource.Unit,
      text: 'My accounts',
      hint: null,
      disabled: false,
      checked: false
    }
  ];

  submitFormError = false;
  pageForm: UntypedFormGroup;

  preSelectedPaymentType$ = this.unitPaymentService.preSelectedPaymentType$;
  preSelectedBankAccount$ = this.unitPaymentService.preSelectedBankAccountId$;

  selectedBankAccount: UnitBankAccountListItem = null;

  unitBankAccountsBesidesSender$: Observable<UnitBankAccountListItem[]>;
  unitBankAccounts$ = this.unitPaymentService.bankAccounts$
    .pipe(
      startWith(null),
      tap(banks => {
        const selectedBankId = this.pageForm.get('senderBankAccount').value;
        if (selectedBankId) {
          this.unitBankAccountsBesidesSender$ = this.filterDestinationBankAccount(banks, selectedBankId);
        } else {
          this.unitBankAccountsBesidesSender$ = of(banks);
        }
      })
    );

  isLoading$ = combineLatest([this._isLoading$, this.unitBankAccounts$]).pipe(
    map(([isLoading, bankAccounts]) => {
      return isLoading || !bankAccounts;
    })
  );
  isEditMode$ = this._existingPayment$.pipe(
    map(existingPayment => !!existingPayment)
  );
  generalServerError$ = new BehaviorSubject<boolean>(false);
  accountLimitError$ = new BehaviorSubject<boolean>(false);
  selectedExistingRecipient$ = new BehaviorSubject<RecipientReadDto>(null);
  selectedAccountLimits$ = new ReplaySubject<UnitAccountLimits>(1);

  // enums
  UnitPaymentType = PaymentType;
  UnitPaymentCategory = UnitPaymentCategory;
  preSelectedAccountName: string = null;

  wireTransactionFee = 8;

  sourceBankEnableWirePayment = true;
  addressFormSettings = this.generateAddressFormSettings();

  accountProvider$ = new BehaviorSubject<UnitAccountProvider>(UnitAccountProvider.Thread);
  userAccountDetails$ = this.userService.accountDetails$;
  bankingProviderString$ = combineLatest(this.userAccountDetails$, this.accountProvider$).pipe(
    switchMap(([user, accountProvider]) => {
      const orgAccountProvider = user?.organizationDetails?.unitAccountProvider;
      const res = accountProvider == null ? (orgAccountProvider == null ? UnitAccountProvider.Thread : orgAccountProvider) : accountProvider;
      return of(UnitAccountProvider.toProvidedBy(res));
    })
  );

  get selectedDestinationAccount() {
    const recipientAccountId = this.pageForm.get('destinationBankAccount')?.value;
    return recipientAccountId ? this.unitBankAccountsBesidesSender$.pipe(
      untilComponentDestroyed(this),
      map(bankAccounts => bankAccounts.find(ba => ba.id === recipientAccountId))
    ) : of(null);
  }

  initialDescriptionLengthAch = 90;
  initialDescriptionLengthWire = 50;
  memoMaxLength = TerraUtils.consts.validators.MEMO_MAX_LENGTH;

  descriptionLength = this.initialDescriptionLengthAch;
  memoLength = this.memoMaxLength;
  memoTooltipText = 'This Memo / Special Instruction also known as, OBI (Originating Beneficiary Information), "Message/Reference to Beneficiary" will be sent along with the wire information and will inform the beneficiary as to additional instructions (for example, which customer account number to apply the funds to).  If you are wiring to a brokerage firm that uses a bank to receive funds, such as National Financial Services or Charles Schwab, enter the "For Further Credit" (FFC) information in this field to ensure that your funds are applied to the proper brokerage account.';

  constructor(private fb: UntypedFormBuilder,
              private unitPaymentService: UnitPaymentService,
              private router: Router,
              private routingService: RoutingService,
              private vcr: ViewContainerRef,
              private dialog: MatDialog,
              private numberPipe: TerraNumberPipe,
              private logger: LoggerService,
              private _location: Location,
              private userService: UserService,
              private utilsService: UtilsService,
              private generalSettingService: GeneralSettingsService,
              private cdr: ChangeDetectorRef,
              private sourceTypeService: HelpSourceTypeService
  ) {
    super();
    this._idempotencyKey = TerraUtils.uuidv4();
    this.generalSettingService.GetByKey('WireTransactionFee')
      .pipe(take(1))
      .subscribe(
        val => {
          this.wireTransactionFee = parseFloat(val) || 8;
          this.initPaymentTypeMethod();
        }
      );
  }

  ngOnInit(): void {
    this.unitPaymentService.setPageTitle('Send New Payment');
    this.unitPaymentService.paymentId$.pipe(
      take(1),
      filter(paymentId => !!paymentId),
      tap(_ => this._isLoading$.next(true)),
      finalize(() => this._isLoading$.next(false)),
      switchMapTo(this._existingPayment$)
    ).subscribe(payment => {
        this.unitPaymentService.setPageTitle('Edit Payment');
        if (payment.status === UnitPaymentStatus.OtpVerified) {
          if (this.unitPaymentService.isGp) {
            this.router.navigateByUrl(this.routingService.paymentReviewPage(payment.id));
          } else {
            this.router.navigateByUrl(this.routingService.lpUnitPaymentReviewPage(payment.id));
          }
        } else if (payment.status === UnitPaymentStatus.Sent) {
          if (this.unitPaymentService.isGp) {
            this.router.navigateByUrl(this.routingService.paymentConfirmationPage(payment.id));
          } else {
            this.router.navigateByUrl(this.routingService.lpUnitPaymentConfirmationPage(payment.id));
          }
        }
      },
      err => {
        if (this.unitPaymentService.isGp) {
          this.router.navigateByUrl(this.routingService.createCrePayment());
        } else {
          this.router.navigateByUrl(this.routingService.lpCreateUnitPayment());
        }
        this.logger.error('Error occurred getting existing payment details', err);
      });

    this.generateFrom();
    this.populatePaymentDetails();
    this.checkIfToRepeatPayment();
  }

  private generateAddressFormSettings(): AddressFormSettings {
    // tslint:disable-next-line:no-bitwise
    const fieldsToShow = AddressFields.Country | AddressFields.Street | AddressFields.Street2 | AddressFields.City | AddressFields.State | AddressFields.PostalCode;
    return new AddressFormSettings(fieldsToShow, undefined, true);
  }

  /**
   * Generate form
   */
  private generateFrom(): void {
    this.pageForm = this.fb.group({
      senderBankAccount: [null, [Validators.required]],
      recipientLastName: [null, [Validators.maxLength(40), CustomValidators.containsForbiddenCharacterValidator(this.forbiddenNameCharacters)]],
      paymentType: [null, [Validators.required]],
      routingNumber: [null, [CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit), Validators.pattern(`^\\d{9}$`)]],
      accountNumber: [null, [CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit), Validators.pattern(`^[0-9]*$`)]],
      confirmAccountNumber: [null, [CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit),
        Validators.pattern(`^[0-9]*$`)]],
      memo: [null, [Validators.maxLength(this.memoMaxLength)]],
      recipientFirstName: [null, [CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.External),
        Validators.maxLength(40), CustomValidators.containsForbiddenCharacterValidator(this.forbiddenNameCharacters)]],
      amount: [null, [Validators.required]],
      description: ['', [Validators.required, Validators.maxLength(this.initialDescriptionLengthAch)]],
      internalNotes: [null, [Validators.maxLength(400)]],
      category: [null, [Validators.required]],
      referenceNumber: [null],
      accountsSource: AccountsSource.NewExternal,
      destinationBankAccount: [null, [CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value === AccountsSource.Unit)]],
      address: {}
      // this.fb.group({
      //   id: null,
      //   streetName: null,
      //   street2Name: null,
      //   houseNumber: null,
      //   cityName: null,
      //   postalCode: null,
      //   stateName: null,
      //   countryId: null
      // }),
    });

    this.pageForm.get('confirmAccountNumber').addValidators([CustomValidators.confirmedValidator(this.pageForm.get('accountNumber') as UntypedFormControl)]);

    combineLatest([this.pageForm.get('senderBankAccount').valueChanges, this.unitBankAccounts$])
      .pipe(
        untilComponentDestroyed(this),
        filter(([bankAccountId, bankAccounts]) => !!bankAccountId && !!bankAccounts && bankAccounts.length > 0)
      )
      .subscribe(([bankAccountId, bankAccounts]) => {
        this.selectedBankAccount = bankAccounts?.find(ba => ba?.id === bankAccountId);
        this.sourceBankEnableWirePayment = this.selectedBankAccount.provider !== UnitAccountProvider.Choice;
        if (!!this.selectedBankAccount) {
          this.accountProvider$.next(this.selectedBankAccount.provider);
          this.accountSelectorTitle = this.selectedBankAccount.holdingName;
          this.selectedAccountLimits$.next(this.selectedBankAccount.accountLimits);
          this.pageForm.get('amount').setValidators([
            Validators.required,
            CustomValidators.minFormattedNumber(0)
          ]);
          this.pageForm.get('amount').updateValueAndValidity();
          this.unitBankAccountsBesidesSender$ = this.filterDestinationBankAccount(bankAccounts, bankAccountId);
          const recipientAccountId = this.pageForm.get('destinationBankAccount').value;
          const recipientAccount = bankAccounts.find(ba => ba.id === recipientAccountId);
          if (recipientAccountId === this.selectedBankAccount?.id || recipientAccount?.holdingId !== this.selectedBankAccount?.holdingId) {
            this.pageForm.get('destinationBankAccount').reset();
          }
        } else {
          this.accountSelectorTitle = this._defaultSelector;
          this.unitBankAccountsBesidesSender$ = of(bankAccounts);
        }
      });

    this.pageForm.get('senderBankAccount').valueChanges.pipe(
      untilComponentDestroyed(this),
      withLatestFrom(this.unitBankAccounts$),
      filter(([bankAccountId, bankAccounts]) => !!bankAccountId && !!bankAccounts && bankAccounts.length > 0)
    ).subscribe(([bankAccountId, bankAccounts]) => {

      this.initPaymentTypeMethod();
      if (this._isRepeated) {
        this.clearSelectedRecipient();
      }
      this._isRepeated = false;
    });

    this.pageForm.get('accountsSource').valueChanges.pipe(
      untilComponentDestroyed(this)
    ).subscribe(accountSource => {
      this.initPaymentTypeMethod();
      this.pageForm.get('paymentType').setValue(null);
      this.pageForm.get('routingNumber').setValidators([CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit), Validators.pattern(`^\\d{9}$`)]);
      this.pageForm.get('accountNumber').setValidators([CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit), Validators.pattern(`^[0-9]*$`)]);
      this.pageForm.get('confirmAccountNumber').setValidators([CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit),
        Validators.pattern(`^[0-9]*$`), CustomValidators.confirmedValidator(this.pageForm.get('accountNumber') as UntypedFormControl)]);
      this.pageForm.get('recipientFirstName').setValidators([
        CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value !== AccountsSource.Unit),
        Validators.maxLength(40), CustomValidators.containsForbiddenCharacterValidator(this.forbiddenNameCharacters)
      ]);
      this.pageForm.get('destinationBankAccount').setValidators([CustomValidators.requiredIf(() => this.pageForm.get('accountsSource').value === AccountsSource.Unit)]);

      this.clearSelectedRecipient();
      this.pageForm.get('destinationBankAccount').reset();
      this.pageForm.updateValueAndValidity();
    });

    this.pageForm.get('paymentType').valueChanges.pipe(
      untilComponentDestroyed(this)
    )
      .subscribe(paymentType => {
        if (paymentType === PaymentType.Wire) {
          this.pageForm.get('address').enable();
          this.pageForm.get('amount').setValidators([
            Validators.required,
            CustomValidators.maxFormattedNumber((this.selectedBankAccount.balance / 100) -
            this.wireTransactionFee),
            CustomValidators.secondaryMaxFormattedNumber(Math.min(
              this.selectedBankAccount.accountLimits.wireLimits.limits.dailyTransfer -
              this.selectedBankAccount.accountLimits.wireLimits.totalsDaily?.transfers,
              this.selectedBankAccount.accountLimits.wireLimits.limits.monthlyTransfer -
              this.selectedBankAccount.accountLimits.wireLimits.totalsMonthly?.transfers) / 100),
            CustomValidators.minFormattedNumber(0)
          ]);
        }
        else if(paymentType === PaymentType.Ach){
          this.pageForm.get('address').disable();
          this.pageForm.get('amount').setValidators([
            Validators.required,
            CustomValidators.maxFormattedNumber(this.selectedBankAccount.balance / 100),
            CustomValidators.secondaryMaxFormattedNumber(Math.min(
              this.selectedBankAccount.accountLimits.achLimits.limits.dailyCredit -
              this.selectedBankAccount.accountLimits.achLimits.totalsDaily?.credits,
              this.selectedBankAccount.accountLimits.achLimits.limits.monthlyCredit -
              this.selectedBankAccount.accountLimits.achLimits.totalsMonthly?.credits) / 100),
            CustomValidators.minFormattedNumber(0)
          ]);
        }
        else {
          this.pageForm.get('address').disable();
        }
      });

    this.pageForm.get('destinationBankAccount').valueChanges.pipe(
      untilComponentDestroyed(this),
      filter(destinationBankAccount => !!destinationBankAccount),
      switchMap(destinationBankAccountId => this.unitBankAccountsBesidesSender$
        .pipe(map(
          unitBankAccountsBesidesSender => unitBankAccountsBesidesSender.find(accountsBesidesSender => accountsBesidesSender.id === destinationBankAccountId)
        )))
    ).subscribe(
      destinationBankAccount => {
        this.pageForm.get('paymentType').setValue(null);
        this.initPaymentTypeMethod(destinationBankAccount.provider !== UnitAccountProvider.Choice);
      }
    );

    this.unitBankAccounts$.pipe(
      untilComponentDestroyed(this),
      filter(accounts => !!accounts),
      withLatestFrom(this.preSelectedBankAccount$),
    ).subscribe(([accounts, selectedBankAccount]) => {
      if (accounts.length === 1) {
        this.pageForm.get('senderBankAccount').setValue(accounts[0].id);
        this.pageForm.get('senderBankAccount').disable();
      } else if (!!selectedBankAccount) {
        const preSelectedBankAccount = accounts.find(a => a.id === selectedBankAccount);
        if (preSelectedBankAccount) {
          this.pageForm.get('senderBankAccount').setValue(selectedBankAccount);
          this.preSelectedAccountName = preSelectedBankAccount.accountNickname;
        }
      }
    });

    this.pageForm.get('paymentType').valueChanges.pipe(
      untilComponentDestroyed(this)
    ).subscribe(
      paymentType => {
        const memoLength = this.pageForm.get('memo')?.value?.length;
        if (paymentType === PaymentType.Ach) {
          this.descriptionLength = this.initialDescriptionLengthAch;
        } else if (paymentType === PaymentType.Wire) {
          this.descriptionLength = this.initialDescriptionLengthWire - (memoLength ? memoLength + 3 : 0);
        }
        this.pageForm.get('description').setValidators([Validators.required, Validators.maxLength(this.descriptionLength)]);
      }
    );

    this.pageForm.get('memo').valueChanges.pipe(
      untilComponentDestroyed(this)
    ).subscribe(
      memo => {
        this.descriptionLength = this.initialDescriptionLengthWire - (memo?.length ? memo.length + 3 : 0);
        this.pageForm.get('description').setValidators([Validators.required, Validators.maxLength(this.descriptionLength)]);
      }
    );
  }

  initPaymentTypeMethod(destinationBankEnableWirePayment: boolean = true) {
    this.pageForm.get('paymentType').enable();
    this.paymentMethodDetails = [
      {
        value: PaymentType.Ach,
        text: 'ACH',
        hint: '3 business days, no fee',
        disabled: false,
        checked: false
      },
      {
        value: PaymentType.Wire,
        text: 'Wire',
        hint: `1 business day, $${this.wireTransactionFee} fee`,
        disabled: !this.sourceBankEnableWirePayment || !destinationBankEnableWirePayment,
        checked: false
      }
    ];

    if (!this.sourceBankEnableWirePayment || !destinationBankEnableWirePayment) {
      this.pageForm.get('paymentType').setValue(PaymentType.Ach);
      this.pageForm.get('paymentType').updateValueAndValidity();
    }
  }

  /**
   * Generates a PaymentCreateDto from form values
   */
  private generateSubmitModel(): PaymentCreateDto {
    const model = new PaymentCreateDto();
    const formValues = this.pageForm.getRawValue();
    model.sourceUnitBankAccountId = formValues.senderBankAccount;
    model.amount = this.numberPipe.parse(formValues.amount);
    model.description = formValues.description || null;
    model.internalNotes = formValues.internalNotes;
    model.category = formValues.category;
    model.referenceNumber = formValues.referenceNumber;
    model.idempotencyKey = this._idempotencyKey;
    model.purpose = UnitPaymentPurpose.VendorPayment;
    model.paymentType = formValues.paymentType;

    // recipient
    if (!this.pageForm.controls.destinationBankAccount?.value) {
      model.recipientId = !!this.selectedExistingRecipient$.value ? this.selectedExistingRecipient$.value.id : undefined;
      model.recipient = this.createRecipientDto(formValues.recipientFirstName, formValues.recipientLastName || '',
        formValues.paymentType === PaymentType.Wire ? {...formValues.address} as LocationDetailsResponse : undefined,
        formValues.memo, !model.recipientId ? this.createRecipientBankAccountDto(formValues.routingNumber, formValues.accountNumber,
          formValues.paymentType) : undefined);
    } else {
      this.selectedDestinationAccount.pipe(
        untilComponentDestroyed(this),
        take(1)
      ).subscribe(selectedBank => {
        model.destinationUnitBankAccountId = selectedBank.id;
        model.purpose = UnitPaymentPurpose.InternalPayment;
      });
    }
    return model;
  }

  private createRecipientDto(firstName: string, lastName: string, location: LocationDetailsResponse | undefined,
    memo: string, bankAccount: DestinationBankAccountCreateDto | undefined) : RecipientCreateDto {
      return {
        firstName,
        lastName,
        location,
        memo,
        bankAccount
      } as RecipientCreateDto
  }

  private createRecipientBankAccountDto(usAbaRoutingNumber: string, accountNumber: string, paymentType: PaymentType) : DestinationBankAccountCreateDto {
      return {
        usAbaRoutingNumber,
        accountNumber,
        paymentType
      } as DestinationBankAccountCreateDto
  }

  /**
   * Populates the form with existing payment info
   */
  private populatePaymentDetails(): void {
    combineLatest([this._existingPayment$, this.unitBankAccounts$]).pipe(
      untilComponentDestroyed(this),
      distinctUntilChanged(),
      filter(([payment, bankAccounts]) => !!payment),
    ).subscribe(([payment, bankAccounts]) => {
      this.pageForm.patchValue({
        senderBankAccount: payment.sourceUnitBankAccountId,
        amount: payment.amount,
        description: payment.description || '',
        internalNotes: payment.internalNotes,
        category: payment.category,
        referenceNumber: payment.referenceNumber
      });

      this.pageForm.get('accountsSource').setValue(!!payment.destinationUnitBankAccountId ? AccountsSource.Unit : AccountsSource.External);
      this._idempotencyKey = payment.idempotencyKey;
      this.populateFormWithSelectedRecipientDetails(payment.recipient);
    });
  }

  /**
   * Submits payment to server
   */
  public submit(): void {
    this.submitFormError = true;
    this.generalServerError$.next(false);
    this.accountLimitError$.next(false);
    if (this.pageForm.invalid) {
      return;
    }

    this._isLoading$.next(true);
    const model = this.generateSubmitModel();
    this.isEditMode$.pipe(
      take(1),
      switchMap(isEdit => {
        if (isEdit) {
          return this._existingPayment$.pipe(
            switchMap(payment => this.unitPaymentService.updatePayment(payment.id, model))
          );
        } else {
          return this.unitPaymentService.sendPayment(model);
        }
      })
    ).subscribe(payment => {
        this._isLoading$.next(false);
        if (this.unitPaymentService.isGp) {
          this.router.navigateByUrl(this.routingService.paymentReviewPage(payment.id));
        } else {
          this.router.navigateByUrl(this.routingService.lpUnitPaymentReviewPage(payment.id));
        }
      },
      error => {
        if (error.status === 402) {
          this.accountLimitError$.next(true);
        } else if (error instanceof BaseResponseDto) {
          this.utilsService.alertErrorMessage(error);
        } else {
          this.logger.error('An error occurred while creating/updating a payment from Unit bank account', error);
          this.generalServerError$.next(true);
        }

        this._isLoading$.next(false);
        return EMPTY;
      });
  }

  /**
   * Adds a recipient from existing recipients list to this payment
   */
  public addExistingRecipient() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = false;
    dialogConfig.panelClass = 'unit-payment-recipient-dialog';


    if (!!this.selectedBankAccount && this.selectedBankAccount.provider !== UnitAccountProvider.Thread) {
      dialogConfig.data = {
        paymentType: PaymentType.Ach
      };
    } else {
      dialogConfig.data = {
        paymentType: PaymentType.None
      };
    }

    this.dialog.open(SelectExistingRecipientDialogComponent, dialogConfig).afterClosed().pipe(
      take(1),
      tap(recipient => {
        if (!!recipient) {
          this.populateFormWithSelectedRecipientDetails(recipient);
        } else {
          this.checkIfToRepeatPayment();
        }
      })
    ).subscribe();
  }

  /**
   * Populate form with an existing recipient details and disable inputs
   * @param recipient Selected recipient
   */
  private populateFormWithSelectedRecipientDetails(recipient: RecipientReadDto) {
    if (!recipient) {
      return;
    }

    this.pageForm.patchValue({
      recipientFirstName: recipient.firstName,
      recipientLastName: recipient.lastName,
      routingNumber: recipient.bankAccount.usAbaRoutingNumber,
      accountNumber: recipient.bankAccount.accountNumber,
      confirmAccountNumber: recipient.bankAccount.accountNumber,
      paymentType: recipient.bankAccount.paymentType === PaymentType.Wire ? PaymentType.Wire : PaymentType.Ach,
      memo: recipient.memo,
      address: recipient.contactLocation
    });
    this.pageForm.get('recipientFirstName').disable();
    this.pageForm.get('recipientLastName').disable();
    this.pageForm.get('routingNumber').disable();
    this.pageForm.get('accountNumber').disable();
    this.pageForm.get('confirmAccountNumber').disable();
    this.pageForm.get('paymentType').disable();
    this.paymentMethodDetails = [
      {
        value: PaymentType.Ach,
        text: 'ACH',
        hint: '3 business days, no fee',
        disabled: true,
        checked: false
      },
      {
        value: PaymentType.Wire,
        text: 'Wire',
        hint: `1 business day, $${this.wireTransactionFee} fee`,
        disabled: true,
        checked: false
      }
    ];
    this.selectedExistingRecipient$.next(recipient);
    this.cdr.markForCheck();
  }

  addressFormReady(event) {
    if (!!this.selectedExistingRecipient$.value) {
      this.pageForm.get('address').patchValue(this.selectedExistingRecipient$.value.contactLocation);
    }
  }

  /**
   * Removes existing recipient details from the form and enables the inputs
   */
  public clearSelectedRecipient() {
    this.pageForm.patchValue({
      recipientFirstName: '',
      recipientLastName: '',
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
      paymentType: null,
    });
    this.pageForm.setControl('address', this.fb.group({}));
    this.pageForm.get('recipientFirstName').enable();
    this.pageForm.get('recipientLastName').enable();
    this.pageForm.get('routingNumber').enable();
    this.pageForm.get('accountNumber').enable();
    this.pageForm.get('confirmAccountNumber').enable();
    this.selectedExistingRecipient$.next(null);
  }

  /**
   * Checks if need to repeat an existing payment
   */
  private checkIfToRepeatPayment() {
    this.unitPaymentService.paymentIdToRepeat$.pipe(
      take(1),
      filter(paymentId => !!paymentId)
    ).subscribe(paymentId => {
      this.repeatPayment(paymentId);
    });
  }

  /**
   * Populates from with existing payment details
   * @param paymentId Payment id to repeat
   */
  private repeatPayment(paymentId: number) {
    this._isLoading$.next(true);
    this._isRepeated = true;
    this.unitPaymentService.getPaymentById(paymentId).pipe(
      take(1),
      finalize(() => this._isLoading$.next(false))
    ).subscribe(payment => {
        this.pageForm.patchValue({
          senderBankAccount: payment.sourceUnitBankAccountId,
          accountsSource: AccountsSource.External,
          amount: payment.amount,
          description: payment.description,
          internalNotes: payment.internalNotes,
          category: payment.category,
          referenceNumber: payment.referenceNumber
        });
        this.populateFormWithSelectedRecipientDetails(payment.recipient);
      },
      error => {
        this.logger.error(`Failed to repeat payment id: ${paymentId}`, error);
      });
  }

  private filterDestinationBankAccount(banks: UnitBankAccountListItem[], accountId: number) {
    const bankHoldingId = banks?.find(ba => ba.id === accountId)?.holdingId;
    return of(banks?.filter(ba => ba.holdingId === bankHoldingId && ba.id !== accountId));
  }

  goBack() {
    this._location.back();
  }

  openUnitAccountLimit(accountLimits: UnitAccountLimits) {
    const config = new MatDialogConfig();
    config.disableClose = true;
    const data = new UnitBankAccountLimitDialogParams();
    data.accountLimits = accountLimits;
    data.displayDailyDebit = true;
    data.displayMonthlyDebit = true;
    config.data = data;
    this.dialog.open(UnitBankAccountLimitDialogComponent, config);
  }

  subTextOption(account: UnitBankAccountListItem) {
    return `$${this.numberPipe.transform(account.available / 100)} / ${AccountType.toString(account.accountType)} ${account.accountNumber.slice(-4).padStart(8, '*')}`;
  }

  talkToUS() {
    const paymentType = this.paymentMethodDetails.find(item => item.value == this.pageForm.get('paymentType').value).text
    const title = `Limit Reached for ${paymentType} on ${this.preSelectedAccountName} Account`
    this.sourceTypeService.issueInput$.next(title);
    const body = `Hello Covercy\nI would like to request an adjustment to my account limits to enable an/a ${paymentType} transfer of ${this.pageForm.get('amount').value}.`
    this.sourceTypeService.content$.next(body)
  }
}
